import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  VideoCamera, Plus, ArrowsClockwise, Copy, Check, CaretDown, Image as ImageIcon, FilmStrip, X,
  PencilSimple, FloppyDisk, Lightning,
} from "@phosphor-icons/react";
import { getToken } from "@/lib/authToken";
import { pickMobileFirst, isMobileES, normalizeESPhone } from "@/lib/phones";

const STATUS_STYLES = {
  created: "bg-slate-100 text-slate-700 border-slate-200",
  waiting: "bg-amber-50 text-amber-800 border-amber-200",
  live: "bg-emerald-50 text-emerald-800 border-emerald-200",
  finished: "bg-emerald-100 text-emerald-900 border-emerald-300",
  stopped: "bg-slate-100 text-slate-700 border-slate-200",
  canceled: "bg-rose-50 text-rose-800 border-rose-200",
};
const STATUS_LABEL = {
  created: "Creada",
  waiting: "Esperando cliente",
  live: "En directo",
  finished: "Finalizada",
  stopped: "Detenida",
  canceled: "Cancelada",
};

export default function VideoperitajeTab({ exp, onExpReload }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/expedientes/${exp.id}/videoperitaje/sessions`);
      setSessions(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [exp.id]);

  const ed = exp.extracted_data || {};
  // Para Avizor/Videoperitación SIEMPRE preferimos un móvil (6/7). El perito
  // puede editar inline el número si el extraído no es correcto.
  const defaultPhone = pickMobileFirst(ed.asegurado_movil, ed.asegurado_telefono);
  const [phone, setPhone] = useState(defaultPhone);
  const [editing, setEditing] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);

  // Refresca el valor si cambia el expediente (nuevo defaultPhone).
  useEffect(() => { setPhone(defaultPhone); }, [defaultPhone]);

  const phoneNorm = normalizeESPhone(phone);
  const hasPhone = !!phoneNorm;
  const phoneIsMobile = isMobileES(phone);
  const [creating, setCreating] = useState(false);

  const savePhone = async () => {
    if (!phoneNorm) {
      toast.error("Introduce un número antes de guardar");
      return;
    }
    setSavingPhone(true);
    try {
      // _mirror_telefono_movil en el backend rellena ambos campos.
      await api.put(`/expedientes/${exp.id}`, {
        extracted_data: { asegurado_movil: phoneNorm },
      });
      toast.success("Teléfono guardado en el expediente");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingPhone(false);
    }
  };

  const startNative = async () => {
    if (!hasPhone) {
      setErr("Falta el teléfono del asegurado. Edítalo antes de iniciar.");
      return;
    }
    setErr("");
    setCreating(true);
    try {
      const r = await api.post(`/expedientes/${exp.id}/videoperitaje/sessions`, {
        provider: "ekspertium_video",
        session_type: "video",
        customer_phone: phoneNorm,
      });
      setSessions((arr) => [r.data, ...arr]);
      // Abrir directamente la sala del perito en pestaña nueva (rol perito con JWT 4h)
      if (r.data?.join_url) {
        window.open(r.data.join_url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error creando la sesión");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">Proveedor</div>
          <h2 className="font-display text-xl font-bold tracking-tight inline-flex items-center gap-2">
            <VideoCamera className="w-5 h-5 text-[#B45309]" weight="duotone" /> Ekspertium Video
            <span className="text-[9px] uppercase tracking-widest font-bold rounded px-1.5 py-0.5 bg-[#FDF0DB] text-[#B45309]">★ NATIVO</span>
          </h2>
          <p className="text-[12px] text-muted mt-1">
            Crea la sesión, envía el SMS al móvil del asegurado y abre la sala del perito
            en una pestaña nueva. Las fotos capturadas y el certificado final se vinculan
            automáticamente al expediente (carpeta <b>Eksvidi</b>).
          </p>
          {/* Contacto editable inline. SIEMPRE móvil (España: 6/7). */}
          <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="vp-contact-row">
            <span className="text-[11px] uppercase tracking-wider text-muted font-semibold">
              Móvil del asegurado:
            </span>
            {editing ? (
              <>
                <input
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="vp-phone-input"
                  className="h-7 text-[12.5px] px-2 border border-border rounded-md bg-white w-40"
                  placeholder="612345678"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={savePhone}
                  disabled={savingPhone || !phoneNorm}
                  data-testid="vp-phone-save"
                  className="h-7 text-[11px] inline-flex items-center gap-1 px-2 rounded-md bg-foreground text-white font-semibold disabled:opacity-50"
                >
                  <FloppyDisk className="w-3 h-3" weight="bold" />
                  {savingPhone ? "Guardando…" : "Guardar"}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setPhone(defaultPhone); }}
                  data-testid="vp-phone-cancel"
                  className="h-7 text-[11px] px-2 rounded-md border border-border text-muted hover:text-foreground"
                >
                  Cancelar
                </button>
              </>
            ) : (
              <>
                {phoneNorm ? (
                  <b className="text-foreground" data-testid="vp-phone-current">{phone}</b>
                ) : (
                  <span className="text-rose-700 text-[12px]">Sin número</span>
                )}
                {phoneNorm && !phoneIsMobile && (
                  <span
                    className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"
                    title="No parece móvil español (no empieza por 6 o 7). El SMS puede fallar."
                  >
                    No es móvil
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  data-testid="vp-phone-edit"
                  className="text-[11px] inline-flex items-center gap-1 text-brand hover:underline"
                >
                  <PencilSimple className="w-3 h-3" weight="bold" />
                  {phoneNorm ? "Editar" : "Añadir"}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="vp-refresh"
            className="text-[11px] border border-border hover:border-foreground rounded-md px-3 py-1.5 inline-flex items-center gap-1.5"
          >
            <ArrowsClockwise className="w-3 h-3" weight="bold" /> Actualizar
          </button>
          <button
            onClick={startNative}
            disabled={creating || !hasPhone}
            data-testid="vp-start-native"
            className="text-[11px] bg-brand text-white hover:opacity-90 disabled:opacity-50 rounded-md px-3.5 py-1.5 inline-flex items-center gap-1.5 transition"
            title={!hasPhone ? "Falta teléfono del asegurado" : "Iniciar videoperitación nativa"}
          >
            <VideoCamera className="w-3 h-3" weight="bold" /> {creating ? "Creando..." : "Iniciar Ekspertium Video"}
          </button>
          <button
            onClick={() => setShowForm((v) => !v)}
            data-testid="vp-new"
            className="text-[11px] border border-border hover:border-foreground rounded-md px-3 py-1.5 inline-flex items-center gap-1.5"
            title="Otros proveedores y opciones avanzadas"
          >
            <Plus className="w-3 h-3" weight="bold" /> Otros…
          </button>
        </div>
      </header>

      {err && <div className="bg-status-error-bg text-status-error-fg text-[12px] px-3 py-2 rounded-md">{err}</div>}

      {showForm && (
        <NewSessionForm
          exp={exp}
          onCreated={(s) => { setSessions((arr) => [s, ...arr]); setShowForm(false); }}
          onCancel={() => setShowForm(false)}
          onError={setErr}
        />
      )}

      {loading ? (
        <div className="text-center py-10 text-muted text-sm">Cargando...</div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md text-sm text-muted">
          Aún no hay sesiones de videoperitaje. Pulsa <b>Nueva sesión</b> para crear la primera.
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((s) => (
            <SessionCard key={s.id} session={s} exp={exp} onReload={load} onExpReload={onExpReload} />
          ))}
        </div>
      )}

      {/* Histórico Avizor del expediente — visible para gestores y para el
          perito asignado (4b). Incluye lanzados/aceptados/rechazados/expirados
          con tiempo de respuesta. */}
      <AvizorHistory expId={exp.id} />
    </div>
  );
}

// ====================================================================
// AvizorHistory — Tarjeta con el histórico de Avizores del expediente
// ====================================================================
const AVIZOR_STATUS_STYLES = {
  pending:  "bg-amber-50 text-amber-800 border-amber-200",
  accepted: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-50 text-rose-800 border-rose-200",
  expired:  "bg-slate-100 text-slate-600 border-slate-200",
};
const AVIZOR_STATUS_LABEL = {
  pending:  "Pendiente",
  accepted: "Aceptado",
  rejected: "Rechazado",
  expired:  "Expirado",
};

function fmtAvizorResponseTime(created, responded) {
  if (!created || !responded) return "—";
  try {
    const a = new Date(created);
    const b = new Date(responded);
    const ms = b - a;
    if (isNaN(ms) || ms < 0) return "—";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    return `${m}m ${rs}s`;
  } catch { return "—"; }
}

function AvizorHistory({ expId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/avizor?expediente_id=${expId}&limit=50`)
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [expId]);

  if (loading) return null;
  if (!items.length) return null;

  const counts = items.reduce((acc, it) => {
    acc[it.status] = (acc[it.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <section
      className="bg-white border border-border rounded-lg overflow-hidden"
      data-testid="avizor-history-card"
    >
      <header className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border/70 bg-amber-50/40">
        <div className="inline-flex items-center gap-2">
          <span className="icon-chip bg-amber-100 text-amber-800">
            <Lightning className="w-4 h-4" weight="fill" />
          </span>
          <div>
            <h3 className="section-title">Histórico Avizor</h3>
            <div className="text-[11px] text-muted">
              {items.length} solicitud{items.length === 1 ? "" : "es"}
              {counts.accepted ? ` · ${counts.accepted} aceptada${counts.accepted === 1 ? "" : "s"}` : ""}
              {counts.rejected ? ` · ${counts.rejected} rechazada${counts.rejected === 1 ? "" : "s"}` : ""}
              {counts.expired ? ` · ${counts.expired} expirada${counts.expired === 1 ? "" : "s"}` : ""}
            </div>
          </div>
        </div>
      </header>
      <ul className="divide-y divide-border/40">
        {items.map((av) => {
          const cls = AVIZOR_STATUS_STYLES[av.status] || AVIZOR_STATUS_STYLES.pending;
          const label = AVIZOR_STATUS_LABEL[av.status] || av.status;
          return (
            <li
              key={av.id}
              data-testid={`avizor-history-row-${av.id}`}
              className="px-4 py-2.5 flex items-start gap-3 text-[12.5px]"
            >
              <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border flex-shrink-0 mt-0.5 ${cls}`}>
                {label}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="font-semibold text-foreground">
                    {av.tipo === "asegurado" ? "Avizor asegurado" : "Avizor reparador"}
                  </span>
                  <span className="text-muted text-[11px]">·</span>
                  <span className="text-muted text-[11.5px]">
                    {av.gestor_name || "Gestor"} → {av.perito_name || "Perito"}
                  </span>
                  <span className="text-muted text-[11px]">·</span>
                  <span className="text-muted text-[11px] tabular-nums">
                    {av.created_at ? new Date(av.created_at).toLocaleString("es-ES", {
                      day: "2-digit", month: "2-digit", year: "2-digit",
                      hour: "2-digit", minute: "2-digit",
                    }) : "—"}
                  </span>
                  {av.responded_at && (
                    <>
                      <span className="text-muted text-[11px]">·</span>
                      <span className="text-muted text-[11px]">
                        respuesta en <b className="tabular-nums">{fmtAvizorResponseTime(av.created_at, av.responded_at)}</b>
                      </span>
                    </>
                  )}
                </div>
                {av.response_reason && (
                  <div className="mt-1 text-[11.5px] text-rose-700 italic line-clamp-2">
                    Motivo rechazo: {av.response_reason}
                  </div>
                )}
                {av.message && (
                  <div className="mt-1 text-[11.5px] text-muted italic line-clamp-2">
                    "{av.message}"
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function NewSessionForm({ exp, onCreated, onCancel, onError }) {
  const ed = exp.extracted_data || {};
  const [phone, setPhone] = useState(pickMobileFirst(ed.asegurado_movil, ed.asegurado_telefono));
  const [email, setEmail] = useState(ed.asegurado_email || "");
  const [provider, setProvider] = useState("ekspertium_video");
  const [type, setType] = useState("live");
  const [guide, setGuide] = useState("home_basic");
  const [busy, setBusy] = useState(false);

  // Ambos proveedores esperan el número móvil nacional de 9 dígitos, sin
  // prefijo ni símbolos.
  const normalizePhone = (v) => {
    if (!v) return null;
    const digits = v.replace(/\D/g, "");
    if (!digits) return null;
    return digits.slice(-9);
  };

  const submit = async () => {
    onError("");
    setBusy(true);
    try {
      const r = await api.post(`/expedientes/${exp.id}/videoperitaje/sessions`, {
        provider,
        session_type: type,
        guide_template: type === "guided" ? guide : null,
        customer_phone: normalizePhone(phone) || null,
        customer_email: email || null,
      });
      const joinUrl = r.data?.join_url || r.data?.request_url;
      if (provider === "ekspertium_video") {
        // Sala nativa — solo iframe, no abrimos pestaña.
      } else if (provider === "tramitasiniestro") {
        if (joinUrl) window.open(joinUrl, "_blank", "noopener,noreferrer");
      } else {
        const url = r.data?.request_url;
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      }
      onCreated(r.data);
    } catch (e) {
      onError(e?.response?.data?.detail || "Error creando la sesión");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-md p-4 space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">Nueva sesión</div>

      {/* Selector de proveedor */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-muted font-semibold">Proveedor</span>
        <div className="inline-flex border border-border rounded-md overflow-hidden bg-white">
          {[
            { id: "ekspertium_video", label: "Ekspertium Video", recommended: true },
            { id: "videoperitaje", label: "Videoperitaje.com" },
            { id: "tramitasiniestro", label: "TramitaSiniestro" },
          ].map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setProvider(p.id)}
              data-testid={`vp-provider-${p.id}`}
              className={`text-[11px] px-3 py-1.5 font-semibold transition-colors inline-flex items-center gap-1.5 ${
                provider === p.id
                  ? "bg-foreground text-white"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              {p.recommended && (
                <span
                  className={`text-[8.5px] px-1.5 py-0.5 rounded ${
                    provider === p.id
                      ? "bg-[#FDF0DB] text-[#B45309]"
                      : "bg-[#FDF0DB] text-[#B45309]"
                  }`}
                  title="Plataforma nativa recomendada"
                >★ NATIVO</span>
              )}
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-[12px] space-y-1">
          <span className="font-semibold">Teléfono del asegurado</span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="692 000 000"
            className="w-full"
            data-testid="vp-phone"
          />
          <span className="text-[10px] text-muted">
            Se enviará un SMS con el enlace al móvil del asegurado.
          </span>
        </label>
        <label className="text-[12px] space-y-1">
          <span className="font-semibold">Email del asegurado (opcional)</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="asegurado@correo.es"
            className="w-full"
            data-testid="vp-email"
          />
        </label>
        {provider === "videoperitaje" && (
          <label className="text-[12px] space-y-1">
            <span className="font-semibold">Tipo de sesión</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full"
              data-testid="vp-type"
            >
              <option value="live">En directo 1-a-1 (live)</option>
              <option value="guided">Autoinspección guiada</option>
            </select>
          </label>
        )}
        {provider === "videoperitaje" && type === "guided" && (
          <label className="text-[12px] space-y-1">
            <span className="font-semibold">Guía</span>
            <select
              value={guide}
              onChange={(e) => setGuide(e.target.value)}
              className="w-full"
              data-testid="vp-guide"
            >
              <option value="home_basic">Hogar (home_basic)</option>
              <option value="car_basic">Vehículo (car_basic)</option>
            </select>
          </label>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-[11px] px-3 py-1.5 text-muted hover:text-foreground"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="vp-submit"
          className="text-[11px] bg-foreground text-white hover:bg-foreground/90 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <VideoCamera className="w-3 h-3" weight="bold" />
          {busy ? "Enviando..." : "Iniciar sesión"}
        </button>
      </div>
    </div>
  );
}

function SessionCard({ session, exp, onReload, onExpReload }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [fetchingFinal, setFetchingFinal] = useState(false);
  const [fetchErr, setFetchErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const isTramita = session.provider === "tramitasiniestro";
  const isEkspVideo = session.provider === "ekspertium_video";

  const styles = STATUS_STYLES[session.status] || STATUS_STYLES.created;
  const label = STATUS_LABEL[session.status] || session.status;
  const token = getToken();

  const copyLink = async () => {
    const link = session.request_url;
    if (!link) return;
    try { await navigator.clipboard.writeText(link); } catch (e) { console.warn("[videoperitaje] clipboard:", e?.message); }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const loadAssets = async () => {
    try {
      const r = await api.get(`/expedientes/${exp.id}/videoperitaje/sessions/${session.id}/assets`);
      setAssets(r.data || []);
      setAssetsLoaded(true);
    } catch (e) { console.warn("[videoperitaje] loadAssets:", e?.message); }
  };

  const fetchFinal = async () => {
    setFetchingFinal(true);
    setFetchErr("");
    try {
      const r = await api.post(`/expedientes/${exp.id}/videoperitaje/sessions/${session.id}/fetch-final`);
      // El nuevo shape es { assets, summary } pero seguimos aceptando arrays
      // por compatibilidad con sesiones antiguas/otros providers.
      const data = r.data;
      const assetsArr = Array.isArray(data) ? data : (data?.assets || []);
      setAssets(assetsArr);
      setAssetsLoaded(true);
      const s = !Array.isArray(data) ? data?.summary : null;
      if (s?.photos) {
        const { imported, skipped_existing: skipped, failed, reported } = s.photos;
        if (imported > 0) {
          toast.success(
            `Importadas ${imported} foto${imported === 1 ? "" : "s"} al expediente${
              s.certificate_imported ? " · certificado actualizado" : ""
            }`,
          );
        } else if (reported > 0 && skipped > 0 && !failed) {
          toast(`${skipped} foto${skipped === 1 ? " ya estaba" : "s ya estaban"} importada${skipped === 1 ? "" : "s"}`);
        } else if (failed > 0) {
          toast.error(`No se pudieron descargar ${failed} foto${failed === 1 ? "" : "s"}`);
        } else if (s.certificate_imported) {
          toast.success("Certificado importado · no hay fotos en la sesión");
        } else {
          toast("La sesión no tiene fotos ni certificado disponibles aún");
        }
      }
    } catch (e) {
      setFetchErr(e?.response?.data?.detail || "No se pudieron descargar los archivos finales");
    } finally {
      setFetchingFinal(false);
    }
  };

  // Auto-sync silencioso para Ekspertium-Video: Eksvidi actualmente NO
  // dispara los webhooks `photo_captured` ni `finalized`, así que cada
  // 30 s hacemos un pull manual mientras la sesión no está cerrada.
  // Cuando lleguen fotos nuevas aparecerán solas en Fotos/Documentos.
  useEffect(() => {
    if (!isEkspVideo) return undefined;
    if (["completed", "cancelled", "failed"].includes(session.status)) return undefined;

    let cancelled = false;
    let prevPhotoCount = null;

    const silentSync = async () => {
      try {
        const r = await api.post(
          `/expedientes/${exp.id}/videoperitaje/sessions/${session.id}/fetch-final`,
        );
        if (cancelled) return;
        const data = r.data;
        const assetsArr = Array.isArray(data) ? data : (data?.assets || []);
        setAssets(assetsArr);
        setAssetsLoaded(true);
        const s = !Array.isArray(data) ? data?.summary : null;
        const imported = s?.photos?.imported || 0;
        // Solo notificar (sin toast intrusivo) si hay algo nuevo respecto
        // al ciclo anterior. Evita spamear.
        if (prevPhotoCount !== null && imported > 0) {
          toast.success(`Sincronizadas ${imported} foto${imported === 1 ? "" : "s"} nueva${imported === 1 ? "" : "s"} de Eksvidi`);
          onReload?.();
          // Refresca el expediente entero para que aparezcan las nuevas
          // fotos y certificados en las pestañas Fotos/Documentos sin
          // tener que recargar la página.
          onExpReload?.();
        }
        prevPhotoCount = imported;
      } catch (_e) {
        // Silencioso: no molestamos al perito si un pull falla.
      }
    };

    // Primer pull inmediato + cada 30 s
    silentSync();
    const t = setInterval(silentSync, 30000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id, session.status, isEkspVideo, exp.id]);

  const refreshState = async () => {
    setRefreshing(true);
    try {
      await api.post(`/expedientes/${exp.id}/videoperitaje/sessions/${session.id}/refresh`);
      onReload?.();
      // Recargar assets también (pueden haberse descargado fotos)
      await loadAssets();
    } catch (e) { console.warn("[videoperitaje] refreshState:", e?.message); }
    finally { setRefreshing(false); }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !assetsLoaded) loadAssets();
  };

  const dt = new Date(session.created_at);

  return (
    <div className="bg-white border border-border rounded-md overflow-hidden" data-testid={`vp-session-${session.id}`}>
      <button onClick={toggle} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-surface/50 transition-colors">
        <VideoCamera className="w-4 h-4 text-muted flex-shrink-0" weight="duotone" />
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] uppercase tracking-widest font-bold rounded px-1.5 py-0.5 ${
              isEkspVideo
                ? "bg-[#FDF0DB] text-[#B45309]"
                : isTramita
                  ? "bg-violet-100 text-violet-800"
                  : "bg-emerald-100 text-emerald-800"
            }`}>
              {isEkspVideo ? "★ Ekspertium Video" : isTramita ? "TramitaSiniestro" : "Videoperitaje"}
            </span>
            <span className="font-mono text-[11px] text-muted">{session.reference.slice(0, 8)}</span>
            <span className="text-[10px] text-muted">·</span>
            <span className="text-[11px] text-muted uppercase tracking-widest">{session.session_type}</span>
            {session.customer_phone && (
              <>
                <span className="text-[10px] text-muted">·</span>
                <span className="text-[11px] font-mono">{session.customer_phone}</span>
              </>
            )}
          </div>
          <div className="text-[11px] text-muted mt-0.5">
            {dt.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-widest font-bold border rounded-full px-2 py-0.5 ${styles}`}>
          {label}
        </span>
        <CaretDown className={`w-4 h-4 text-muted transition-transform ${open ? "rotate-180" : ""}`} weight="bold" />
      </button>

      {open && (
        <div className="border-t border-border bg-slate-50/30 p-4 space-y-3">
          {/* Panel nativo Ekspertium-Video: abrir en pestaña + copiar enlace asegurado */}
          {isEkspVideo && session.join_url && (
            <div className="bg-white border border-[#E8EAED] rounded-[10px] p-4 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[11px] uppercase tracking-wide text-[#B45309] font-semibold inline-flex items-center gap-1.5">
                  <VideoCamera className="w-3.5 h-3.5" weight="duotone" /> Ekspertium Video — Sala del perito
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <OpenSalaButton expId={exp.id} session={session} onReload={onReload} />
                </div>
              </div>
              {session.request_url && (
                <div className="text-[11px] text-muted">
                  Enlace para el asegurado (ya enviado por SMS):
                  <div className="mt-1 flex items-center gap-2">
                    <code className="font-mono text-[10.5px] bg-[#FBFBFC] border border-[#E8EAED] px-2 py-1 rounded flex-1 truncate">{session.request_url}</code>
                    <button
                      onClick={async () => { try { await navigator.clipboard.writeText(session.request_url); } catch (_e) { /* clipboard denied */ } }}
                      className="text-[11px] border border-[#E8EAED] hover:border-foreground rounded-md px-2 py-1"
                    >Copiar</button>
                  </div>
                </div>
              )}
              <div className="text-[11px] text-muted">
                Al finalizar, las fotos y el certificado se adjuntarán al expediente automáticamente en la carpeta <b>Eksvidi</b>.
              </div>
            </div>
          )}

          {isTramita && (
            <div className="flex items-center gap-2">
              <button
                onClick={refreshState}
                disabled={refreshing}
                data-testid={`vp-refresh-state-${session.id}`}
                className="text-[11px] border border-violet-300 text-violet-800 hover:border-violet-500 hover:bg-violet-50 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <ArrowsClockwise className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} weight="bold" />
                {refreshing ? "Consultando..." : "Refrescar estado"}
              </button>
              <span className="text-[10px] text-muted">
                Trae el estado actual desde TramitaSiniestro y descarga las fotos si la sesión ha terminado.
              </span>
            </div>
          )}

          {/* Link */}
          {session.request_url && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1">
                URL de la sesión (enlace de un solo uso)
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={session.request_url}
                  readOnly
                  className="font-mono !text-[11px] !py-1.5 flex-1"
                  onClick={(e) => e.target.select()}
                  data-testid={`vp-url-${session.id}`}
                />
                <button
                  onClick={copyLink}
                  data-testid={`vp-copy-${session.id}`}
                  className="text-[11px] border border-border hover:border-foreground rounded-md px-3 py-1.5 inline-flex items-center gap-1.5"
                >
                  {copied ? <Check className="w-3 h-3 text-emerald-600" weight="bold" /> : <Copy className="w-3 h-3" weight="bold" />}
                  {copied ? "Copiado" : "Copiar"}
                </button>
              </div>
            </div>
          )}

          {/* Historia de estados */}
          {session.state_history?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1.5">
                Historia de estados
              </div>
              <ol className="space-y-1">
                {session.state_history.map((h, idx) => (
                  <li key={`${h.status}-${h.date}-${idx}`} className="text-[11px] text-muted flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${STATUS_STYLES[h.status]?.includes("emerald") ? "bg-emerald-500" : STATUS_STYLES[h.status]?.includes("amber") ? "bg-amber-500" : STATUS_STYLES[h.status]?.includes("rose") ? "bg-rose-500" : "bg-slate-400"}`}></span>
                    <span className="font-medium text-foreground">{STATUS_LABEL[h.status] || h.status}</span>
                    <span>{typeof h.date === "number" ? new Date(h.date * 1000).toLocaleString("es-ES") : h.date}</span>
                    {h.auto && <span className="text-[9px] opacity-60 uppercase">auto</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Media recibido */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">
                Capturas y vídeos ({assets.length})
              </div>
              <button
                onClick={fetchFinal}
                disabled={fetchingFinal}
                data-testid={`vp-fetch-final-${session.id}`}
                className="text-[10px] border border-border hover:border-foreground rounded-md px-2 py-1 inline-flex items-center gap-1 disabled:opacity-50"
              >
                <ArrowsClockwise className={`w-3 h-3 ${fetchingFinal ? "animate-spin" : ""}`} weight="bold" />
                {fetchingFinal ? "Descargando..." : "Descargar evidencias finales"}
              </button>
            </div>
            {fetchErr && (
              <div className="text-[10px] text-status-error-fg bg-status-error-bg rounded-md px-2 py-1 mb-1.5">
                {fetchErr}
              </div>
            )}
            {assets.length === 0 ? (
              <div className="text-[11px] text-muted italic">
                Aún no hay archivos. Si la sesión ya está finalizada, pulsa <b>Descargar evidencias finales</b> para traer el PDF y el JSON del proveedor.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {assets.map((a) => (
                  <a
                    key={a.id}
                    href={`${API}/expedientes/${exp.id}/videoperitaje/sessions/${session.id}/assets/${a.id}/file?token=${token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative border border-border rounded-md bg-white overflow-hidden aspect-video flex items-center justify-center"
                    data-testid={`vp-asset-${a.id}`}
                  >
                    {a.kind === "image" ? (
                      <img
                        src={`${API}/expedientes/${exp.id}/videoperitaje/sessions/${session.id}/assets/${a.id}/file?token=${token}`}
                        alt={a.filename}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : a.kind === "video" ? (
                      <FilmStrip className="w-8 h-8 text-slate-400" weight="duotone" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-400" weight="duotone" />
                    )}
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent text-[9px] text-white p-1 truncate">
                      {a.filename}
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * OpenSalaButton — botón "Abrir sala" robusto.
 *
 * Antes era un <a href={session.join_url}> directo, lo que provocaba el
 * bug "Token inválido o expirado" cuando el perito intentaba reabrir
 * la sala más de 4h después de haber aceptado el avizor (vida del JWT
 * de Ekspertium-Video = 4h).
 *
 * Ahora, en cada click:
 *   1. Llama POST /videoperitaje/sessions/{id}/refresh-expert-url para
 *      regenerar el JWT en el servidor.
 *   2. Abre window.open con el join_url recién devuelto.
 *
 * Si el refresh falla, ofrece como fallback abrir el join_url cacheado
 * para no bloquear al perito cuando la plataforma esté temporalmente
 * caída (mejor un intento que nada).
 */
function OpenSalaButton({ expId, session, onReload }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/expedientes/${expId}/videoperitaje/sessions/${session.id}/refresh-expert-url`);
      const url = r.data?.join_url || session.join_url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      onReload?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Error desconocido";
      console.warn("[vp] open-sala refresh failed:", detail);
      toast.error(`No se pudo renovar el acceso (${detail}). Abriendo con el enlace anterior…`);
      try {
        window.open(session.join_url, "_blank", "noopener,noreferrer");
      } catch (e2) { console.warn("[vp] fallback open:", e2?.message); }
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={`vp-open-sala-${session.id}`}
      title="Abre la sala con un acceso recién generado (válido 4 h)"
      className="text-[11px] bg-brand text-white hover:opacity-90 disabled:opacity-50 rounded-md px-3 py-1.5 transition-colors inline-flex items-center gap-1 font-semibold"
    >
      <VideoCamera className="w-3 h-3" weight="bold" />
      {busy ? "Renovando…" : "Abrir sala"}
    </button>
  );
}
